import { z } from "zod";

import { Media } from "@/components/forms/schemas/media";
import { SocialLinkSchema } from "@/components/forms/schemas/social-link";
import { artistEnrichmentReadSchema } from "@/pages/artists/schemas/artist.aienrichment.form";
import { relatedArtistSchema } from "@/pages/artists/schemas/artist.related";
import {
  artistSpotifyEnrichmentSchema,
  artistSpotifyStatsSchema,
} from "@/pages/artists/schemas/artist.spotifyenrichment.form";
import { artistTrackSchema } from "@/pages/artists/schemas/artist.track";
import { genreNestedSchema } from "@/pages/genres/schemas/genre.nested.form";

const STATUS_OPTIONS = [
  "InReview",
  "Incomplete",
  "Complete",
  "Unregistered",
] as const;

/**
 * READ schema for the artist edit/review pages.
 *
 * `.passthrough()` and a lenient nested `enrichment`: this is consumed through
 * `parseRead()`, and before the split it nested the *strict* enrichment schema
 * behind a hard `.parse()`, so one unmodelled enrichment key blanked the entire
 * page. The strict contract lives on `artistEnrichmentSubmitSchema` and is
 * applied where it belongs — just before the PUT.
 */
export const artistViewSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    slug: z.string().nullish(),
    genres: z.array(genreNestedSchema).nullish(),
    about: z.string().nullish(),
    followers: z.coerce.number().default(0),
    socialLinks: z.array(SocialLinkSchema).nullish().default([]),
    status: z.enum(STATUS_OPTIONS).default("Unregistered"),
    media: Media.extend({
      photos: Media.shape.photos.min(0),
    }), // allow photos to be nullish for backward compatibility, but they should be included in the media object
    tracks: z.array(artistTrackSchema).nullish(),
    tags: z.array(z.string()).nullish().default([]),
    hide: z.boolean().default(false),
    // Set to the target's id when a curator merged this artist into another one.
    // A plain nullable string for the same reason `enrichmentStatus` below is: the
    // backend must be free to grow this without an IMS redeploy rejecting the whole
    // artist. There is deliberately no `Merged` entry in STATUS_OPTIONS — merging
    // never writes a new status, it sets this field and `hide`.
    mergedInto: z.string().nullish(),
    curatedFields: z.array(z.string()).nullish(),
    // `.passthrough()`: this is a READ schema, and a strict nested object here
    // strips every key IMS does not model — including the Tina creation stamp
    // the entity trace is rendered from. `metadata` is omitted
    // from `artistEditSchema`, so nothing rides back into a PUT.
    metadata: z
      .object({ spotifyId: z.string().nullish(), source: z.string().nullish() })
      .passthrough()
      .nullish(),
    relatedArtists: z.array(relatedArtistSchema).nullish().default([]),
    enrichment: artistEnrichmentReadSchema.nullish(),
    spotifyEnrichment: artistSpotifyEnrichmentSchema
      .extend({ stats: artistSpotifyStatsSchema.nullish() })
      .nullish(),
    // A plain string, not the enum: a status the backend grows before IMS
    // redeploys must not reject the whole artist. The vocabulary lives in
    // `@/lib/enrichment` and is applied where it is rendered/filtered.
    enrichmentStatus: z.string().nullish(),
    embeddingStatus: z.string().nullish(),
    enrichmentRetryCount: z.coerce.number().nullish(),
    createdBy: z.string().nullish(),
    createdAt: z.string().datetime({ offset: true }),
    updatedBy: z.string().nullish(),
    updatedAt: z.string().datetime({ offset: true }).nullish(),
  })
  .passthrough();

export type ArtistViewType = z.infer<typeof artistViewSchema>;
